import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  GripVertical,
  Image as ImageIcon,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuthStore } from "../context/AuthContext";
import { hasPermission } from "../lib/utils/permissions";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import useDragAutoScroll from "../hooks/useDragAutoScroll";

const normalizeSearchText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const Blogs = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canCreate = hasPermission(user, "blogs", "create");
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isReordering, setIsReordering] = useState(false);
  const [draggedBlogId, setDraggedBlogId] = useState(null);
  const [dropTargetBlogId, setDropTargetBlogId] = useState(null);
  const [search, setSearch] = useState("");
  const filteredBlogs = useMemo(() => {
    const query = normalizeSearchText(search);
    if (!query) return blogs;

    return blogs.filter((blog) =>
      normalizeSearchText([
        blog.title,
        blog.slug,
        blog.author_name,
        blog.author_designation,
        blog.category,
        blog.status,
        blog.description,
        blog.blog_date,
      ].join(" ")).includes(query),
    );
  }, [blogs, search]);
  const canReorder = !isReordering && !search.trim();
  const { handleDragAutoScroll, stopDragAutoScroll } = useDragAutoScroll(
    Boolean(draggedBlogId),
    canReorder,
  );

  const isAdmin = hasPermission(user, "blogs", "list");

  const fetchBlogs = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/blogs?status=all&limit=100&page=1");
      const firstPage = response.data?.data || {};
      const firstItems = firstPage.items || [];
      const totalPages = Number(firstPage.pages) || 1;

      if (totalPages <= 1) {
        setBlogs(firstItems);
        return;
      }

      const remainingPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
          apiClient.get(`/blogs?status=all&limit=100&page=${index + 2}`),
        ),
      );
      setBlogs([
        ...firstItems,
        ...remainingPages.flatMap((pageResponse) => pageResponse.data?.data?.items || []),
      ]);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load blog data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchBlogs();
    }
  }, [isAdmin]);

  const handleDelete = async (blog) => {
    try {
      await apiClient.delete(`/blogs/${blog.id}`);
      showSuccessToast("Blog deleted successfully");
      fetchBlogs();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete blog");
    }
  };

  const persistBlogOrder = async (orderedBlogs) => {
    setIsReordering(true);
    try {
      await apiClient.put("/blogs/reorder", {
        orderedItems: orderedBlogs.map((blog, index) => ({
          id: blog.id,
          display_order: index + 1,
        })),
      });

      showSuccessToast("Blog order updated successfully");
      setBlogs(
        orderedBlogs.map((blog, index) => ({
          ...blog,
          display_order: index + 1,
        })),
      );
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to update blog order");
      fetchBlogs();
    } finally {
      setIsReordering(false);
      setDraggedBlogId(null);
      setDropTargetBlogId(null);
    }
  };

  const handleDropCard = async (event, targetBlogId) => {
    event.preventDefault();
    stopDragAutoScroll();

    if (!canReorder || !draggedBlogId || draggedBlogId === targetBlogId) {
      setDraggedBlogId(null);
      setDropTargetBlogId(null);
      return;
    }

    const draggedIndex = blogs.findIndex((blog) => blog.id === draggedBlogId);
    const targetIndex = blogs.findIndex((blog) => blog.id === targetBlogId);

    if (draggedIndex < 0 || targetIndex < 0) {
      setDraggedBlogId(null);
      setDropTargetBlogId(null);
      return;
    }

    const reordered = [...blogs];
    const [movedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, movedItem);
    setBlogs(reordered);
    await persistBlogOrder(reordered);
  };

  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Blogs</h1>
        <p className="mt-2 text-sm text-slate-500">
          Blog management is only available for admin users.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6" onDragOver={handleDragAutoScroll}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Blog Management
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Manage blog content, images, and author details.
          </p>
          <p className={`mt-2 text-xs font-semibold uppercase tracking-[0.18em] ${search.trim() ? "text-amber-600" : "text-slate-400"}`}>
            {search.trim()
              ? "Clear search to reorder blogs"
              : "Drag cards to reorder blogs; hold near the top or bottom to auto-scroll"}
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={fetchBlogs}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canCreate && (

            <Button
            type="button"
            className="rounded-lg"
            onClick={() => navigate("/blogs/new")}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Blog
          </Button>

          )}
        </div>
      </div>

      <div className="rounded-lg border border-white/60 bg-white/80 p-5 shadow-sm backdrop-blur-xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xl">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search title, author, category, status or content"
              aria-label="Search blogs"
              className="h-11 rounded-lg pl-10 pr-10"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Clear blog search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <p className="shrink-0 text-sm font-semibold text-slate-600">
            Showing {filteredBlogs.length} of {blogs.length} blogs
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">
            Loading blogs...
          </div>
        ) : blogs.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
              <ImageIcon className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">
              No blogs found
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Create your first blog to populate this module.
            </p>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="py-12 text-center">
            <h2 className="text-lg font-semibold text-slate-800">No matching blogs</h2>
            <p className="mt-1 text-sm text-slate-500">Try another title, author, category, status, or keyword.</p>
            <Button type="button" variant="ghost" className="mt-3 rounded-lg text-primary" onClick={() => setSearch("")}>
              <X className="mr-2 h-4 w-4" />
              Clear search
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {filteredBlogs.map((blog) => (
              <div
                key={blog.id}
                draggable={canReorder}
                onDragStart={(event) => {
                  if (!canReorder) {
                    event.preventDefault();
                    return;
                  }
                  setDraggedBlogId(blog.id);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (draggedBlogId && draggedBlogId !== blog.id) {
                    setDropTargetBlogId(blog.id);
                  }
                }}
                onDrop={(event) => handleDropCard(event, blog.id)}
                onDragEnd={() => {
                  stopDragAutoScroll();
                  setDraggedBlogId(null);
                  setDropTargetBlogId(null);
                }}
                className={`overflow-hidden rounded-lg border bg-white shadow-sm transition-all duration-200 ${
                  draggedBlogId === blog.id
                    ? "scale-[0.98] border-primary/40 opacity-60"
                    : dropTargetBlogId === blog.id
                      ? "border-primary ring-2 ring-primary/10 shadow-lg shadow-primary/10"
                      : "border-slate-100"
                }`}
              >
                <div className="grid gap-0 md:grid-cols-[220px_1fr]">
                  <div className="bg-slate-100">
                    {blog.thumbnail_image_url ? (
                      <img
                        src={blog.thumbnail_image_url}
                        alt={blog.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full min-h-48 items-center justify-center text-slate-400">
                        <ImageIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="mt-0.5 rounded-lg border border-slate-200 bg-slate-50 p-2 text-slate-400">
                          <GripVertical className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <h2 className="truncate text-lg font-bold text-slate-800">
                            {blog.title}
                          </h2>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <p className="text-xs font-medium text-slate-500">
                              Order: {blog.display_order ?? "-"}
                            </p>
                            <span
                              className={`rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                                blog.status === "published"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              {blog.status || "published"}
                            </span>
                            {blog.category ? (
                              <span className="text-xs text-slate-400 font-medium">
                                {blog.category}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-xs font-medium text-slate-500">
                      <div className="inline-flex items-center gap-2">
                        <UserRound className="h-4 w-4" />
                        <span>{blog.author_name}</span>
                      </div>
                      <div className="inline-flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{String(blog.blog_date).slice(0, 10)}</span>
                      </div>
                    </div>

                    <p className="text-sm text-slate-500">
                      {blog.author_designation}
                    </p>

                    <div
                      className="line-clamp-3 text-sm text-slate-600"
                      dangerouslySetInnerHTML={{ __html: blog.description }}
                    />

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-lg"
                        onClick={() => navigate(`/blogs/${blog.id}/edit`)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      <DeleteConfirmationButton onConfirm={() => handleDelete(blog)}>
<Button
                        type="button"
                        variant="outline"
                        className="rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
</DeleteConfirmationButton>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
